class PromoProgressBarManager {
  constructor() {
    this.couponCode = null;
    this.goalAmount = 0;
    this.goalVariantId = null;
    this.previousGoalAchieved = false;
    this.couponApplied = false;
    this.variantAdded = false;
    this.cartUpdateUnsubscriber = null;

    this.init();
  }

  static getInstance() {
    if (!window.promoProgressBarManager) {
      window.promoProgressBarManager = new PromoProgressBarManager();
    }
    return window.promoProgressBarManager;
  }

  init() {
    // Clean up existing subscription if it exists
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = null;
    }

    const promoProgressBar = document.querySelector('.promo-progress-bar');
    if (promoProgressBar) {
      this.couponCode = promoProgressBar.dataset.couponCode;
      this.goalAmount = parseFloat(promoProgressBar.dataset.goalAmount) || 0;
      this.goalVariantId = promoProgressBar.dataset.goalVariantId;
    }

    if (this.goalAmount <= 0) {
      return;
    }

    this.checkInitialGoalStatus();

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (response) => {
      if (response.cartData) {
        this.handleCartUpdate(response.cartData);
      }
    });
  }

  checkInitialGoalStatus() {
    const cartTotal = window.Shopify?.cart?.total_price || 0;
    const currentAmount = cartTotal / 100;
    this.previousGoalAchieved = currentAmount >= this.goalAmount;
  }

  async handleCartUpdate(cartData) {
    // Some publishers (e.g., product form add) return section JSON without totals.
    // If totals are missing, fetch the full cart for reliable values.
    if (!cartData || typeof cartData.total_price !== 'number') {
      try {
        const fullCart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        cartData = fullCart;
      } catch (e) {
        console.error('Promo progress bar: Failed to load full cart after update', e);
        return;
      }
    }

    const currentAmount = cartData.total_price / 100;
    const isGoalAchieved = currentAmount >= this.goalAmount;

    if (isGoalAchieved && !this.previousGoalAchieved) {
      if (this.couponCode && !this.couponApplied) {
        this.applyCouponCode();
      }
      if (this.goalVariantId && !this.variantAdded) {
        this.addGoalVariant(cartData);
      }
    } else if (!isGoalAchieved && this.previousGoalAchieved) {
      if (this.variantAdded) {
        this.removeGoalVariant(cartData);
      }
    }

    this.previousGoalAchieved = isGoalAchieved;
  }

  async applyCouponCode() {
    if (!this.couponCode || this.couponApplied) return;

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: this.couponCode,
          sections: 'cart-drawer,cart-icon-bubble',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        this.couponApplied = true;
        this.refreshCartSections(data);
      } else {
        console.error('Promo progress bar: Failed to apply coupon code:', response.statusText);
      }
    } catch (error) {
      console.error('Promo progress bar: Error applying coupon code:', error);
    }
  }

  async addGoalVariant(cartData) {
    if (!this.goalVariantId || this.variantAdded) return;

    // Check if the variant is already in the cart
    const isVariantInCart = (cartData.items || []).some((item) => {
      const variantId = item.variant_id ?? item.id;
      return variantId && variantId.toString() === this.goalVariantId;
    });

    if (isVariantInCart) {
      this.variantAdded = true;
      return;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: this.goalVariantId,
          quantity: 1,
          properties: {
            _promo_progress_bar_reward: 'true',
            _promo_progress_bar_goal_achieved: 'true',
          },
          sections: 'cart-drawer,cart-icon-bubble',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        this.variantAdded = true;
        this.refreshCartSections(data);
      } else {
        const errorData = await response.json().catch(() => ({ errors: 'Unknown error' }));
        console.error('Promo progress bar: Failed to add goal variant:', response.status, errorData);
      }
    } catch (error) {
      console.error('Promo progress bar: Error adding goal variant:', error);
    }
  }

  async removeGoalVariant(cartData) {
    if (!this.goalVariantId || !this.variantAdded) return;

    const rewardItem = (cartData.items || []).find((item) => {
      const variantId = item.variant_id ?? item.id;
      return (
        variantId &&
        variantId.toString() === this.goalVariantId &&
        item.properties &&
        item.properties._promo_progress_bar_reward === 'true'
      );
    });

    if (!rewardItem) {
      this.variantAdded = false;
      return;
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: rewardItem.key,
          quantity: 0,
          sections: 'cart-drawer,cart-icon-bubble',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        this.variantAdded = false;
        this.refreshCartSections(data);
      } else {
        const errorData = await response.json().catch(() => ({ errors: 'Unknown error' }));
        console.error('Promo progress bar: Failed to remove goal variant:', response.status, errorData);
      }
    } catch (error) {
      console.error('Promo progress bar: Error removing goal variant:', error);
    }
  }

  refreshCartSections(data) {
    if (data.sections && data.sections['cart-drawer']) {
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data.sections['cart-drawer'], 'text/html');
        const newCartDrawerItems = doc.querySelector('cart-drawer-items');
        if (newCartDrawerItems) {
          cartDrawerItems.innerHTML = newCartDrawerItems.innerHTML;
        }
      }
    }

    if (data.sections && data.sections['cart-icon-bubble']) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
      }
    }
  }

  destroy() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  PromoProgressBarManager.getInstance();
});
